import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, MapPin } from 'lucide-react';
import { useMusic } from './music/MusicPlayerProvider';
import VenueSignature from './VenueSignature';
const Hero = ({ eventInfo, onReserveClick, onVisibilityChange, onActiveLogoElementChange }) => {
  const sectionRef = useRef(null);
  const compactLogoRef = useRef(null);
  const desktopLogoRef = useRef(null);
  const mobileLogoRef = useRef(null);
  const { playing, setPlaying, seekBy, setMuted, muted } = useMusic();
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, tx: 0, ty: 0 });
  const [isHeroVisible, setIsHeroVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isXl, setIsXl] = useState(false);
  const rafRef = useRef(0);
  const startRef = useRef(0);
  const spinRafRef = useRef(0);
  const lastTsRef = useRef(0);
  const discRef = useRef(null);
  const dragRef = useRef({
    active: false,
    startAngle: 0,
    lastAngle: 0,
    lastTime: 0,
    wasPlaying: false,
    wasMuted: false,
    baseAtStart: 0,
  });
  const manualRotRef = useRef(0);
  const baseRotRef = useRef(0);
  const autoRotRef = useRef(0);
  const audioCtxRef = useRef(null);
  const noiseGainRef = useRef(null);
  const noiseNodeRef = useRef(null);
  useEffect(() => {
    return () => {};
  }, []);
  useEffect(() => {
    const sectionEl = sectionRef.current;
    if (!sectionEl || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeroVisible(entry.isIntersecting);
      },
      {
        threshold: 0.08,
      }
    );

    observer.observe(sectionEl);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') return undefined;

    const mobileQuery = window.matchMedia('(max-width: 767px)');
    const xlQuery = window.matchMedia('(min-width: 1280px)');
    const syncViewportState = () => {
      setIsMobile(mobileQuery.matches);
      setIsXl(xlQuery.matches);
    };

    syncViewportState();

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', syncViewportState);
      xlQuery.addEventListener('change', syncViewportState);
      return () => {
        mobileQuery.removeEventListener('change', syncViewportState);
        xlQuery.removeEventListener('change', syncViewportState);
      };
    }

    mobileQuery.addListener(syncViewportState);
    xlQuery.addListener(syncViewportState);
    return () => {
      mobileQuery.removeListener(syncViewportState);
      xlQuery.removeListener(syncViewportState);
    };
  }, []);
  useEffect(() => {
    const logoEl = isMobile
      ? mobileLogoRef.current
      : isXl
        ? desktopLogoRef.current
        : compactLogoRef.current;

    onActiveLogoElementChange?.(logoEl || null);

    if (!logoEl || typeof IntersectionObserver === 'undefined') {
      onVisibilityChange?.(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        onVisibilityChange?.(entry.isIntersecting && entry.intersectionRatio > 0.05);
      },
      {
        threshold: [0, 0.05, 0.2],
      }
    );

    observer.observe(logoEl);
    return () => {
      observer.disconnect();
      onActiveLogoElementChange?.(null);
    };
  }, [isMobile, isXl, onVisibilityChange, onActiveLogoElementChange]);
  const ensureAudio = () => {
    if (!audioCtxRef.current) {
      try { audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch {}
    }
    return audioCtxRef.current;
  };
  const applyDiscRotation = () => {
    if (!discRef.current) return;
    const nextRotation = (baseRotRef.current + autoRotRef.current + manualRotRef.current) % 360;
    discRef.current.style.transform = `rotate(${nextRotation}deg)`;
  };
  // Use JS-driven rotation to keep angle continuity between drag and auto-spin
  useEffect(() => {
    if (isMobile) {
      if (spinRafRef.current) cancelAnimationFrame(spinRafRef.current);
      return undefined;
    }

    const step = (ts) => {
      const last = lastTsRef.current || ts;
      lastTsRef.current = ts;
      const dt = ts - last;
      if (!dragRef.current.active && playing && isHeroVisible) {
        // 360deg per 20s => 0.018 deg/ms
        const d = 0.018 * dt;
        autoRotRef.current = (autoRotRef.current + d) % 360;
        applyDiscRotation();
      }
      spinRafRef.current = requestAnimationFrame(step);
    };
    applyDiscRotation();
    spinRafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(spinRafRef.current);
  }, [playing, isHeroVisible, isMobile]);
  const onPointerDown = (e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    e.preventDefault();
    e.stopPropagation();
    const discEl = discRef.current;
    if (!discEl) return;
    const rect = discEl.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const ang = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI;
    dragRef.current = { active: true, startAngle: ang, lastAngle: ang, lastTime: Date.now(), wasPlaying: playing, wasMuted: muted, baseAtStart: baseRotRef.current + autoRotRef.current };
    // Keep underlying track playing, but temporarily unmute if muted to hear scrubbing
    try { setMuted(false); } catch {}
    manualRotRef.current = 0;
    applyDiscRotation();
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp, { once: true });
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.active) return;
    const discEl = discRef.current;
    if (!discEl) return;
    const rect = discEl.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const ang = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI;
    let delta = ang - dragRef.current.lastAngle;
    // Normalize to shortest path
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    // Map degrees to seconds (approx 360deg ~ 1.5s)
    const secondsPerDeg = 1.5 / 360;
    seekBy(delta * secondsPerDeg);
    // Set visual rotation relative to start
    const manual = ang - dragRef.current.startAngle;
    manualRotRef.current = manual;
    applyDiscRotation();
    dragRef.current.lastAngle = ang;
    dragRef.current.lastTime = Date.now();
  };
  const onPointerUp = () => {
    dragRef.current.active = false;
    // Commit manual rotation to base, keep continuity
    baseRotRef.current = (baseRotRef.current + manualRotRef.current) % 360;
    manualRotRef.current = 0;
    applyDiscRotation();
    // Restore mute state
    try { setMuted(dragRef.current.wasMuted); } catch {}
    window.removeEventListener('pointermove', onPointerMove);
  };

  const meta = [];
  if (eventInfo.currentVolume.date) meta.push({ icon: Calendar, text: eventInfo.currentVolume.date });
  if (eventInfo.currentVolume.time) meta.push({ icon: Clock, text: eventInfo.currentVolume.time });
  if (eventInfo.currentVolume.date || eventInfo.currentVolume.time) meta.push({ icon: MapPin, text: 'Le Phare du Kanaal' });
  const editionLabel = `Vol.${eventInfo.currentVolume.number}`;
  const themeLabel = eventInfo.currentVolume.theme;

  const renderMetaRow = (compact = false, embedded = false) => {
    if (meta.length === 0) return null;

    if (compact && embedded) {
      return (
        <div className="grid grid-cols-1 gap-2 justify-items-center w-full text-[11px] text-white/68">
          {meta.map((m, i) => {
            const Icon = m.icon;
            return (
              <span
                key={`${m.text}-${i}`}
                className="inline-flex gap-2 justify-center items-center px-3 py-1.5 rounded-full border border-white/8 bg-white/[0.02]"
              >
                <Icon size={13} className="opacity-50" />
                <span>{m.text}</span>
              </span>
            );
          })}
        </div>
      );
    }

    return (
      <div
        className={
          embedded
            ? compact
              ? 'inline-flex flex-wrap gap-x-3 gap-y-2 justify-center items-center text-[11px] text-white/68'
              : 'inline-flex flex-wrap gap-x-4 gap-y-2 justify-center items-center text-sm text-white/68'
            : compact
              ? 'inline-flex flex-wrap gap-x-3 gap-y-2 justify-center items-center px-4 py-2.5 rounded-full border border-white/10 bg-black/36 text-[11px] text-white/78'
              : 'inline-flex flex-wrap gap-x-4 gap-y-2 justify-center items-center px-5 py-3 rounded-full border border-white/10 bg-black/30 text-sm text-white/78 backdrop-blur-sm'
        }
      >
        {meta.map((m, i) => {
          const Icon = m.icon;
          return (
            <React.Fragment key={`${m.text}-${i}`}>
              {i > 0 && <span aria-hidden="true" className="hidden w-1 h-1 rounded-full bg-white/14 sm:block" />}
              <span className="inline-flex gap-2 items-center">
                <Icon size={compact ? 13 : 15} className="opacity-50" />
                <span>{m.text}</span>
              </span>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderActionContent = (compact = false) => {
    return null;
  };

  const renderPanelText = (text, className, tone = 'strong') => {
    const ghostToneClass = tone === 'strong'
      ? 'text-white/22'
      : 'text-white/14';

    return (
      <span className={`inline-flex relative justify-center ${className}`}>
        <span
          aria-hidden="true"
          className={`absolute inset-0 translate-x-[0.04em] translate-y-[0.03em] blur-[1.4px] ${ghostToneClass}`}
        >
          {text}
        </span>
        <span
          aria-hidden="true"
          className={`absolute inset-0 -translate-x-[0.03em] translate-y-[-0.02em] opacity-45 ${tone === 'strong' ? 'text-white/28' : 'text-white/18'}`}
        >
          {text}
        </span>
        <span className="relative">{text}</span>
      </span>
    );
  };

  const renderEventPanel = (compact = false, vinyl = false) => {
    const metaContent = renderMetaRow(compact, true);
    const actionContent = renderActionContent(compact);
    const hasLowerContent = Boolean(metaContent || actionContent);

    return (
      <div
        className={
          vinyl
            ? 'inline-flex flex-col gap-4 items-center px-7 pt-8 pb-5 w-full text-center'
            : compact
              ? 'inline-flex flex-col gap-3 items-center px-4 py-5 w-full text-center max-w-[19rem]'
              : 'inline-flex flex-col gap-5 items-center px-7 py-6 w-full text-center'
        }
      >
        <div className={compact ? 'flex flex-col gap-1.5 items-center w-full' : 'flex flex-col gap-2 items-center w-full'}>
          <div className={compact ? 'inline-flex items-center text-[0.92rem] uppercase tracking-[0.24em] text-white/34' : `inline-flex items-center uppercase ${vinyl ? 'text-[1.38rem] tracking-[0.34em] text-white/28' : 'text-[11px] tracking-[0.3em] text-white/34'}`}>
            {renderPanelText(editionLabel, 'font-medium', 'soft')}
            <span aria-hidden="true" className={compact ? 'mx-2.5 w-4 h-px bg-white/14' : vinyl ? 'mx-3 w-12 h-px bg-white/12' : 'mx-3 w-8 h-px bg-white/14'} />
            {renderPanelText(
              themeLabel,
              compact
                ? 'max-w-[16rem] text-[0.92rem] leading-[1.35] font-semibold uppercase tracking-[0.14em] text-white/84'
                : vinyl
                  ? 'max-w-[24rem] text-[1.38rem] font-semibold uppercase tracking-[0.3em] text-white/88'
                  : 'max-w-[26rem] text-[1.06rem] font-semibold uppercase tracking-[0.22em] text-white/84',
              'strong'
            )}
          </div>
          
        </div>
        {hasLowerContent && (
          <div className={vinyl ? 'w-full h-px bg-white/5' : 'w-full h-px bg-white/6'} aria-hidden="true" />
        )}
        {metaContent}
        {actionContent && (
          <>
            <div className={compact ? 'my-0 w-10 h-px bg-white/8' : vinyl ? 'w-24 h-px bg-white/7' : 'w-16 h-px bg-white/8'} aria-hidden="true" />
            {actionContent}
          </>
        )}
      </div>
    );
  };

  return (
    <section
      ref={sectionRef}
      className="flex isolate overflow-hidden relative z-20 justify-center items-center"
      style={{
        perspective: '1200px',
        height: 'calc(var(--app-height) + var(--app-safe-bottom) + 1px)',
        minHeight: 'calc(var(--app-height) + var(--app-safe-bottom) + 1px)',
        paddingTop: 'max(4.5rem, env(safe-area-inset-top, 0px))',
        paddingBottom: 'max(1rem, calc(var(--app-safe-bottom) + 0.5rem))'
      }}
    >
      {/* Smoke Effect Background */}
      <div className="absolute inset-0 bg-black">
        <div className="absolute inset-0 opacity-16 md:opacity-30">
          <div className="hidden absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-br to-transparent rounded-full blur-3xl smoke-effect from-gray-600/20 md:block animate-float" />
          <div className="hidden absolute right-1/4 bottom-1/4 w-80 h-80 bg-gradient-to-tl to-transparent rounded-full blur-3xl smoke-effect from-gray-500/20 md:block animate-float-delayed" />
          <div className={`smoke-effect absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${isMobile ? 'opacity-60 w-[280px] h-[280px]' : 'w-[600px] h-[600px]'} bg-gradient-to-r from-gray-700/8 via-gray-600/8 to-gray-700/8 rounded-full ${isMobile ? 'blur-2xl' : 'blur-3xl animate-pulse-slow'}`} />
        </div>
      </div>

      {/* Rotating CD (base layer) */}
      <div className="absolute bottom-0 left-1/2 z-[2] pointer-events-none" style={{ transform: isMobile ? 'translate(-50%, 34%)' : 'translate(-50%, 38%)' }}>
        <div className="relative" style={{ transform: 'none', transformStyle: 'preserve-3d' }}>
          <div
            aria-hidden
            id="hero-disc"
            ref={discRef}
            onPointerDown={onPointerDown}
            className={`relative rounded-full aspect-square w-[min(118vw,700px)] md:w-[min(190vw,1700px)]
              ${isMobile ? 'pointer-events-none' : 'pointer-events-auto cursor-grab active:cursor-grabbing'}
              transform-gpu will-change-transform
              shadow-[0_0_80px_12px_rgba(255,255,255,0.04)_inset,0_0_55px_0_rgba(0,0,0,0.55)] md:shadow-[0_0_120px_20px_rgba(255,255,255,0.05)_inset,0_0_80px_0_rgba(0,0,0,0.6)]
              bg-[conic-gradient(at_50%_50%,#0b0b0b_0deg,#1a1a1a_45deg,#2f2f2f_90deg,#1a1a1a_135deg,#0f0f0f_180deg,#1a1a1a_225deg,#2f2f2f_270deg,#1a1a1a_315deg,#0b0b0b_360deg)]
              before:content-[''] before:absolute before:inset-0 before:rounded-full
              before:bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.12),transparent_40%),radial-gradient(circle_at_30%_70%,rgba(255,255,255,0.06),transparent_45%)]
            `}
            style={{
              touchAction: isMobile ? 'auto' : 'none',
              contain: 'paint',
              animation: isMobile ? 'spin 20s linear infinite' : 'none',
              animationPlayState: isMobile && playing && isHeroVisible ? 'running' : 'paused',
            }}
          >
          <div
            className={`absolute inset-0 rounded-full ${isMobile ? 'opacity-16' : 'opacity-30'}`}
            style={{
              backgroundImage:
                'repeating-radial-gradient(circle at 50% 50%, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 3px)'
            }}
          />
          {/* Fine radial stripes to enhance motion perception (outer band only) */}
          {!isMobile && (
            <div
              className="absolute inset-0 rounded-full opacity-20 mix-blend-screen pointer-events-none"
              style={{
                backgroundImage:
                  'repeating-conic-gradient(from 0deg, rgba(255,255,255,0.08) 0deg 0.5deg, transparent 0.5deg 6deg)',
                WebkitMaskImage: 'radial-gradient(circle at 50% 50%, transparent 0 60%, black 78%, transparent 96%)',
                maskImage: 'radial-gradient(circle at 50% 50%, transparent 0 60%, black 78%, transparent 96%)'
              }}
            />
          )}
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(circle at 50% 50%, transparent 0 92%, rgba(230,230,230,0.35) 94%, rgba(218,165,32,0.18) 96%, transparent 100%)',
              boxShadow:
                isMobile
                  ? 'inset 0 0 0 1px rgba(255,255,255,0.12), 0 0 0 1px rgba(255,255,255,0.04)'
                  : 'inset 0 0 0 2px rgba(255,255,255,0.14), 0 0 0 2px rgba(255,255,255,0.06), 0 0 38px rgba(255,215,0,0.08)',
              filter: isMobile ? 'none' : 'drop-shadow(0 0 24px rgba(255,255,255,0.05))'
            }}
          />
          {!isMobile && (
            <div
              className="absolute inset-0 rounded-full mix-blend-screen pointer-events-none opacity-55"
              style={{
                backgroundImage:
                  'conic-gradient(from 0deg, rgba(255,255,255,0.16) 0deg, transparent 24deg, transparent 156deg, rgba(255,255,255,0.12) 180deg, transparent 336deg, rgba(255,255,255,0.16) 360deg)',
                WebkitMaskImage: 'radial-gradient(circle at 50% 50%, transparent 0 54%, black 66%)',
                maskImage: 'radial-gradient(circle at 50% 50%, transparent 0 54%, black 66%)'
              }}
            />
          )}
          {/* Subtle edge ticks for clearer rotation (very faint) */}
          {!isMobile && (
            <div
              className="absolute inset-0 rounded-full mix-blend-screen pointer-events-none opacity-15"
              style={{
                backgroundImage:
                  'repeating-conic-gradient(from 0deg, rgba(255,255,255,0.12) 0deg 0.25deg, transparent 0.25deg 5deg)',
                WebkitMaskImage: 'radial-gradient(circle at 50% 50%, transparent 0 88%, black 93%, transparent 100%)',
                maskImage: 'radial-gradient(circle at 50% 50%, transparent 0 88%, black 93%, transparent 100%)'
              }}
            />
          )}
          <div className="absolute inset-0 rounded-full pointer-events-none shadow-[0_0_0_1px_rgba(255,255,255,0.06)_inset,0_0_0_1px_rgba(255,255,255,0.04)]" />
          {/* Label ring */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
              w-[36%] aspect-square rounded-full border border-white/10 shadow-[0_0_20px_rgba(255,255,255,0.06)_inset]"
            style={{
              background:
                'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.05) 36%, transparent 72%), repeating-linear-gradient(135deg, rgba(255,255,255,0.05) 0 2px, transparent 2px 6px), #0e0e0e'
            }}
          />
          {/* Inner paper label */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[26%] aspect-square rounded-full border border-white/10"
            style={{
              background:
                'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.06) 40%, rgba(0,0,0,0.25) 100%), conic-gradient(from 20deg, #111 0 60deg, #191919 60deg 120deg, #111 120deg 180deg, #1a1a1a 180deg 240deg, #111 240deg 300deg, #191919 300deg 360deg)',
              boxShadow: 'inset 0 0 24px rgba(0,0,0,0.3), 0 0 18px rgba(255,255,255,0.05)'
            }}
          />
          {/* Center hole */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[3.8%] aspect-square rounded-full bg-black shadow-[0_0_0_2px_rgba(255,255,255,0.06),0_0_0_6px_rgba(255,255,255,0.04)]"
          />
          {/* Rim gloss */}
          <div
            className="pointer-events-none absolute inset-0 rounded-full
              bg-[radial-gradient(closest-side,transparent_66%,rgba(255,255,255,0.08)_70%,transparent_76%)]"
          />
          </div>
        </div>
      </div>
      {/* Fixed glare above disc */}
      <div className="absolute bottom-0 left-1/2 z-[3] hidden pointer-events-none md:block" style={{ transform: 'translate(-50%, 50%)' }}>
        <div className="relative w-[min(90vw,860px)] aspect-square">
          <div
            className="absolute top-1/2 left-1/2 w-[84%] aspect-square -translate-x-1/2 -translate-y-1/2 rotate-[-20deg] rounded-full opacity-35 mix-blend-screen"
            style={{
              backgroundImage: 'conic-gradient(from 0deg, rgba(255,255,255,0.18) 0deg, transparent 40deg, transparent 320deg, rgba(255,255,255,0.18) 360deg)',
              WebkitMaskImage: 'radial-gradient(circle at 50% 50%, transparent 0 52%, black 62%, transparent 80%)',
              maskImage: 'radial-gradient(circle at 50% 50%, transparent 0 52%, black 62%, transparent 80%)'
            }}
          />
        </div>
      </div>
      {/* Inter-layer shadow */}
      <div className="absolute bottom-0 left-1/2 z-[2] pointer-events-none" style={{ transform: isMobile ? 'translate(-50%, 2%)' : 'translate(-50%, -4%)' }}>
        <div
          className="w-[54vw] h-[7vh] rounded-full blur-xl md:w-[80vw] md:h-[18vh] md:blur-2xl"
          style={{
            background: 'radial-gradient(closest-side, rgba(0,0,0,0.45), transparent 70%)',
            filter: isMobile ? 'blur(10px)' : 'blur(18px)'
          }}
        />
      </div>
      {/* Disc Overlays (desktop and up) */}
      <div className="absolute bottom-0 left-1/2 z-[4] pointer-events-none hidden -translate-x-1/2 md:block">
        <div className="relative w-[min(94vw,1280px)] h-[min(64vh,640px)]">
          <div className="absolute left-1/2 top-[-21.5%] w-[min(74vw,520px)] -translate-x-1/2 pointer-events-auto lg:w-[min(56vw,560px)]">
            <div className="flex justify-center xl:hidden">
              {renderEventPanel(true, true)}
            </div>
            <div className="hidden justify-center xl:flex">
              {renderEventPanel(false, true)}
            </div>
          </div>

          <div className="absolute left-[5%] right-[5%] top-[32%] pointer-events-auto xl:hidden">
            <div className="flex gap-6 justify-between items-start">
              <div className="max-w-[300px] text-left">
                <p className="mb-4 text-[10px] font-medium uppercase tracking-[0.35em] text-white/35">
                  Dining meets sound
                </p>
              <h1 ref={compactLogoRef} className="font-black text-[clamp(3.5rem,8vw,5.25rem)] leading-[0.84] tracking-tighter text-white drop-shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
                  <span className="block">CRA</span>
                  <span className="block">PULE</span>
                </h1>
              </div>

              <div className="pt-2">
                <VenueSignature
                  className="px-4 py-2.5 gap-3.5 opacity-90"
                  labelClassName="text-[10px] tracking-[0.18em]"
                  logoClassName="w-40 h-11 lg:w-44 lg:h-12"
                />
              </div>
            </div>
          </div>

          <div className="absolute left-[-5%] top-[29.5%] w-[min(28vw,380px)] pointer-events-auto hidden xl:block">
            <div className="text-left">
              <p className="mb-4 text-[10px] font-medium uppercase tracking-[0.35em] text-white/35">
                Dining meets sound
              </p>
              <h1 ref={desktopLogoRef} className="font-black text-[clamp(4.25rem,5.6vw,6.5rem)] leading-[0.84] tracking-tighter text-white text-center drop-shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
                <span className="block">CRA</span>
                <span className="block">PULE</span>
              </h1>
            </div>
          </div>

          <div className="absolute right-[-4.5%] top-[33%] w-[min(25vw,360px)] pointer-events-auto hidden xl:flex justify-end">
            <VenueSignature
              className="gap-5 px-5.5 py-3.5 opacity-90"
              labelClassName="text-[11.5px] tracking-[0.2em]"
              logoClassName="w-[11.75rem] h-[3.15rem] 2xl:w-[13.25rem] 2xl:h-[3.7rem]"
            />
          </div>
        </div>
      </div>
      {/* Content */}
      <div
        className="relative z-10 justify-center items-center px-6 mx-auto max-w-4xl text-center"
        style={{
          transform: 'none',
          textShadow: '0 2px 18px rgba(0,0,0,0.5)'
        }}
      >
        {/* Mobile content (simplified layout) */}
        <div className="mx-auto max-w-sm md:hidden">
          <div className="-translate-y-20">
            <h1 ref={mobileLogoRef} className="font-black text-[20vw] leading-[0.83] tracking-tighter mb-5">
              <span className="block">CRA</span>
              <span className="block">PULE</span>
            </h1>
            <VenueSignature
              className="mb-5 px-4 py-2.5 gap-3.5 opacity-90"
              labelClassName="text-[10px] tracking-[0.18em]"
              logoClassName="w-40 h-11"
            />
          </div>
          <div className="mt-1">
            {renderEventPanel(true)}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div
        className="flex absolute right-0 left-0 z-30 justify-center pointer-events-none"
        style={{ bottom: 'max(2.5rem, calc(var(--app-safe-bottom) + 1.5rem))' }}
      >
        <button
          type="button"
          aria-label="Voir la suite"
          onClick={() => {
            const el = document.getElementById('concept');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }}
          className="animate-bounce pointer-events-auto"
        >
          <div className="flex justify-center pt-2 w-6 h-10 rounded-full border-2 border-white/30">
            <div className="w-1 h-2 rounded-full bg-white/50 animate-scroll" />
          </div>
        </button>
      </div>
    </section>
  );
};

export default Hero;
